import { del, get, patch, post, put } from '@/lib/api';
import { toCreditOrFee, creditFeeAmount, toEmailLog } from './invoices';
import {
  num,
  rec,
  str,
  strArray,
  strOrNull,
  toDay,
  unwrapList,
  unwrapObject
} from './normalize';
import type {
  CompanyInfo,
  CreditOrFee,
  GeneralSettings,
  InvoiceEmailLog,
  InvoiceReminders,
  InvoiceSettings,
  UpcomingReminder,
  Paginated,
  ReportFilters,
  ReportOverview,
  ReportResult,
  PlanTier,
  Subscription,
  TeamInvite,
  TeamMember
} from '@/types';

// ---------------------------------------------------------- credits & fees

export interface CreditsFeesQuery {
  page?: number;
  pageSize?: number;
  clientId?: string;
  invoiced?: boolean;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  sortField?: 'date' | 'name' | 'amount' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
}

export async function fetchCreditsAndFees(
  query: CreditsFeesQuery = {},
  signal?: AbortSignal
): Promise<Paginated<CreditOrFee>> {
  const raw = rec(
    await get<unknown>(
      '/api/credits-and-fees',
      {
        page: query.page ?? 1,
        pageSize: query.pageSize ?? 50,
        clientId: query.clientId,
        invoiced: query.invoiced === undefined ? undefined : String(query.invoiced),
        searchText: query.search || undefined,
        dateFrom: query.dateFrom,
        dateTo: query.dateTo,
        sortField: query.sortField ?? 'date',
        sortOrder: query.sortOrder ?? 'desc'
      },
      signal
    )
  );
  const pagination = rec(raw.pagination);
  return {
    items: unwrapList(raw, 'creditsAndFees').map(toCreditOrFee),
    page: num(pagination.page, query.page ?? 1),
    pageSize: num(pagination.pageSize, 50),
    totalCount: num(pagination.totalCount),
    totalPages: num(pagination.totalPages, 1)
  };
}

export type CreditFeeInput = Pick<
  CreditOrFee,
  'clientId' | 'kind' | 'description' | 'amount' | 'date' | 'isHourly'
>;

const creditFeeBody = (input: CreditFeeInput) => ({
  clientId: input.clientId,
  name: input.description,
  amount: creditFeeAmount(input),
  isHourly: input.isHourly,
  date: input.date
});

export const createCreditOrFee = (input: CreditFeeInput) =>
  post<unknown>('/api/credits-and-fees', creditFeeBody(input)).then(toCreditOrFee);

export const updateCreditOrFee = (id: string, input: CreditFeeInput) =>
  put<unknown>(`/api/credits-and-fees/${id}`, creditFeeBody(input)).then(toCreditOrFee);

export const deleteCreditOrFee = (id: string) => del(`/api/credits-and-fees/${id}`);

// ------------------------------------------------------------------ reports

function reportParams(f: ReportFilters) {
  return {
    startDate: f.dateFrom,
    endDate: f.dateTo,
    clientId: f.clientId,
    userId: f.userId,
    activityId: f.activityId
  };
}

export async function fetchReportOverview(
  filters: ReportFilters,
  signal?: AbortSignal
): Promise<ReportOverview> {
  const raw = rec(await get<unknown>('/api/reports/overview', reportParams(filters), signal));
  const o = rec(raw.overview ?? raw);
  return {
    uninvoicedHours: num(o.uninvoicedHours),
    uninvoicedAmount: num(o.potentialRevenue ?? o.uninvoicedAmount),
    invoicedThisMonth: num(o.invoicedAmount),
    revenueThisMonth: num(o.totalRevenue),
    outstandingAmount: num(o.invoicedAmount) - num(o.totalRevenue),
    overdueCount: num(o.overdueInvoices)
  };
}

/**
 * The eight sub-reports each return a differently-named array of differently
 * named fields. They all reduce to `{ label, hours, amount, count }` rows plus
 * an optional time series, so one normalizer serves the whole section.
 */
interface ReportShape {
  key: string;
  id: string;
  label: string;
  /** Endpoint under /api/reports, when it is not the report's own name. */
  path?: string;
  params?: Record<string, string>;
}

const REPORT_SHAPES = {
  'hours-by-client': { key: 'clientBreakdown', id: 'clientId', label: 'clientName' },
  'hours-by-activity': { key: 'activityBreakdown', id: 'activityId', label: 'activityName' },
  'hours-by-employee': { key: 'employeeBreakdown', id: 'userId', label: 'userName' },
  'billable-performance': { key: 'billablePerformance', id: 'billableId', label: 'billableName' },
  'all-hours': { key: 'entries', id: 'id', label: 'activityName' },
  uninvoiced: { key: 'entries', id: 'id', label: 'activityName' },
  'unbilled-by-client': {
    key: 'unbilled',
    id: 'id',
    label: 'name',
    path: 'unbilled-amounts',
    params: { groupBy: 'client' }
  },
  'unbilled-by-member': {
    key: 'unbilled',
    id: 'id',
    label: 'name',
    path: 'unbilled-amounts',
    params: { groupBy: 'member' }
  },
  invoiced: { key: 'invoices', id: 'id', label: 'invoiceNumber' },
  revenue: { key: 'paidInvoices', id: 'id', label: 'invoiceNumber' }
} as const satisfies Record<string, ReportShape>;

export type ReportName = keyof typeof REPORT_SHAPES;

export async function fetchReport(
  name: ReportName,
  filters: ReportFilters,
  signal?: AbortSignal
): Promise<ReportResult> {
  const shape: ReportShape = REPORT_SHAPES[name];
  const raw = rec(
    await get<unknown>(
      `/api/reports/${shape.path ?? name}`,
      { ...reportParams(filters), ...shape.params },
      signal
    )
  );
  const list = unwrapList(raw, shape.key);

  const rows = list.map((v) => {
    const r = rec(v);
    return {
      key: str(r[shape.id] ?? r.id ?? Math.random()),
      label: str(r[shape.label] ?? '—', '—'),
      hours: num(r.totalHours ?? r.time ?? r.hours),
      amount: num(r.totalAmount ?? r.potentialRevenue ?? r.amount),
      count: num(r.totalEntries ?? r.totalInvoices ?? r.entries ?? 1, 1),
      ...(r.oldestDate ? { since: toDay(r.oldestDate) } : {})
    };
  });

  const series = unwrapList(raw, 'timeSeries').map((v) => {
    const p = rec(v);
    return {
      period: toDay(p.date ?? p.period),
      hours: num(p.totalHours ?? p.hours),
      amount: num(p.totalAmount ?? p.amount)
    };
  });

  return {
    rows,
    series,
    totals: rows.reduce(
      (acc, r) => ({
        hours: acc.hours + r.hours,
        amount: acc.amount + r.amount,
        count: acc.count + r.count
      }),
      { hours: 0, amount: 0, count: 0 }
    )
  };
}

/**
 * The daily hours series lives on `/reports/overview`, not on the individual
 * reports — none of the eight sub-report endpoints return a `timeSeries` key.
 */
export async function fetchReportTimeSeries(filters: ReportFilters, signal?: AbortSignal) {
  const raw = rec(await get<unknown>('/api/reports/overview', reportParams(filters), signal));
  return unwrapList(raw, 'timeSeries').map((v) => {
    const p = rec(v);
    return {
      period: toDay(p.date ?? p.period),
      hours: num(p.totalHours ?? p.hours),
      amount: num(p.totalAmount ?? p.amount)
    };
  });
}

export async function fetchReportFilterOptions(signal?: AbortSignal) {
  const raw = rec(await get<unknown>('/api/reports/filters', undefined, signal));
  const pick = (key: string) =>
    unwrapList(raw[key]).map((v) => {
      const o = rec(v);
      return { id: str(o.id), name: str(o.name) };
    });
  return {
    clients: pick('clients'),
    employees: pick('employees'),
    billables: pick('billables')
  };
}

// ------------------------------------------------------------------- emails

/**
 * NOTE FOR apps/server: v1 renders /emails from the `getEmails()` server
 * action; there is no REST route. The SPA needs:
 *   GET /api/emails?page=&pageSize=  -> { data: Email[], pagination }
 *   GET /api/emails/:id              -> { data: Email }
 */
export async function fetchEmails(
  query: { page?: number; pageSize?: number } = {},
  signal?: AbortSignal
): Promise<Paginated<InvoiceEmailLog>> {
  const raw = rec(
    await get<unknown>(
      '/api/emails',
      { page: query.page ?? 1, pageSize: query.pageSize ?? 20 },
      signal
    )
  );
  const pagination = rec(raw.pagination);
  return {
    items: unwrapList(raw, 'emails').map(toEmailLog),
    page: num(pagination.currentPage ?? pagination.page, query.page ?? 1),
    pageSize: num(pagination.pageSize, 20),
    totalCount: num(pagination.totalCount),
    totalPages: num(pagination.totalPages, 1)
  };
}

export async function fetchEmail(id: string, signal?: AbortSignal) {
  const raw = rec(await get<unknown>(`/api/emails/${id}`, undefined, signal));
  return toEmailLog(raw.data ?? raw.email ?? raw);
}

// ----------------------------------------------------------------- settings

export async function fetchCompanyInfo(signal?: AbortSignal): Promise<CompanyInfo> {
  const c = unwrapObject(await get<unknown>('/api/company-info', undefined, signal), 'companyInfo');
  return {
    name: str(c.name),
    addressLine1: strOrNull(c.address ?? c.addressLine1),
    addressLine2: strOrNull(c.addressLine2),
    city: strOrNull(c.city),
    state: strOrNull(c.state),
    postalCode: strOrNull(c.postalCode),
    phone: strOrNull(c.phone),
    email: strOrNull(c.email),
    website: strOrNull(c.website),
    logoUrl: strOrNull(c.logo ?? c.logoUrl)
  };
}

export const saveCompanyInfo = (input: Partial<CompanyInfo>) =>
  post('/api/company-info', {
    name: input.name,
    address: input.addressLine1 === null ? '' : input.addressLine1,
    phone: input.phone === null ? '' : input.phone,
    email: input.email === null ? '' : input.email,
    logo: input.logoUrl
  });

export async function fetchInvoiceSettings(signal?: AbortSignal): Promise<InvoiceSettings> {
  const s = unwrapObject(await get<unknown>('/api/invoice-settings', undefined, signal));
  return {
    defaultHourlyRate: num(s.defaultHourlyRate, 50),
    invoiceNumberPrefix: str(s.invoiceNumberPrefix),
    nextInvoiceNumber: num(s.nextInvoiceNumber, 1),
    paymentTermsDays: num(s.defaultDueDays, 30),
    defaultNotes: strOrNull(s.defaultNotes),
    templateId: str(s.defaultTemplate, 'simple'),
    ccEmails: strArray(s.emailCc),
    bccEmails: strArray(s.emailBcc),
    replyToEmail: strOrNull(s.replyToEmail),
    emailBodyTemplate: strOrNull(s.emailTemplate),
    currency: str(s.currency, 'USD'),
    autoMarkSentOnManualSend: s.autoMarkSentOnManualSend === true,
    reminderBeforeDueEnabled: s.reminderBeforeDueEnabled === true,
    reminderBeforeDueDays: num(s.reminderBeforeDueDays, 3),
    reminderOnDueEnabled: s.reminderOnDueEnabled === true,
    overdueRemindersEnabled: s.enableAutoFollowUp === true,
    overdueReminderDays: Array.isArray(s.overdueReminderDays)
      ? s.overdueReminderDays.map((d) => num(d)).filter((d) => d > 0)
      : [7],
    beforeDueEmailTemplate: strOrNull(s.beforeDueEmailTemplate),
    overdueEmailTemplate: strOrNull(s.followUpEmailTemplate),
    interestEnabled: s.interestEnabled === true,
    interestRate: num(s.followUpInterestRate, 1.5),
    interestPeriod: s.interestPeriod === 'year' ? 'year' : 'month',
    interestGraceDays: num(s.interestGraceDays, 0)
  };
}

export async function fetchUpcomingReminders(signal?: AbortSignal): Promise<UpcomingReminder[]> {
  const raw = await get<unknown>('/api/reminders/upcoming', undefined, signal);
  return unwrapList(raw).map((v) => {
    const r = rec(v);
    return {
      kind: str(r.kind) as UpcomingReminder['kind'],
      offsetDays: num(r.offsetDays),
      date: str(r.date),
      invoiceId: str(r.invoiceId),
      invoiceNumber: str(r.invoiceNumber)
    };
  });
}

function toInvoiceReminders(raw: unknown): InvoiceReminders {
  const r = unwrapObject(raw);
  const planned = (v: unknown) => {
    const p = rec(v);
    return {
      kind: str(p.kind) as InvoiceReminders['schedule'][number]['kind'],
      offsetDays: num(p.offsetDays),
      date: str(p.date)
    };
  };
  return {
    paused: r.paused === true,
    eligible: r.eligible === true,
    schedule: unwrapList(r.schedule).map((v) => {
      const p = rec(v);
      return {
        ...planned(p),
        sentAt: strOrNull(p.sentAt),
        status: p.status === 'sent' ? 'sent' : p.status === 'missed' ? 'missed' : 'upcoming'
      };
    }),
    next: r.next ? planned(r.next) : null
  };
}

export const fetchInvoiceReminders = (invoiceId: string, signal?: AbortSignal) =>
  get<unknown>(`/api/invoices/${invoiceId}/reminders`, undefined, signal).then(toInvoiceReminders);

export const setInvoiceRemindersPaused = (invoiceId: string, paused: boolean) =>
  patch<unknown>(`/api/invoices/${invoiceId}/reminders`, { paused }).then(toInvoiceReminders);

export const saveInvoiceSettings = (input: Partial<InvoiceSettings>) =>
  post('/api/invoice-settings', {
    defaultHourlyRate: input.defaultHourlyRate?.toFixed(2),
    defaultDueDays: input.paymentTermsDays,
    defaultTemplate: input.templateId,
    emailCc: input.ccEmails,
    emailBcc: input.bccEmails,
    // '' clears it back to the company email; undefined leaves it alone.
    replyToEmail: input.replyToEmail === null ? '' : input.replyToEmail,
    emailTemplate: input.emailBodyTemplate,
    defaultNotes: input.defaultNotes,
    autoMarkSentOnManualSend: input.autoMarkSentOnManualSend,
    reminderBeforeDueEnabled: input.reminderBeforeDueEnabled,
    reminderBeforeDueDays: input.reminderBeforeDueDays,
    reminderOnDueEnabled: input.reminderOnDueEnabled,
    enableAutoFollowUp: input.overdueRemindersEnabled,
    overdueReminderDays: input.overdueReminderDays,
    beforeDueEmailTemplate: input.beforeDueEmailTemplate,
    followUpEmailTemplate: input.overdueEmailTemplate,
    interestEnabled: input.interestEnabled,
    followUpInterestRate: input.interestRate,
    interestPeriod: input.interestPeriod,
    interestGraceDays: input.interestGraceDays
  });

export async function fetchGeneralSettings(signal?: AbortSignal): Promise<GeneralSettings> {
  const s = unwrapObject(await get<unknown>('/api/general-settings', undefined, signal));
  return {
    useEmployeeColors: s.useEmployeeColors === true,
    employeeColors: unwrapList(s.employeeColors).map((v) => {
      const c = rec(v);
      return {
        userId: str(c.userId),
        userName: strOrNull(c.userName),
        color: str(c.color, '#e4e4e7')
      };
    }),
    teamBillingType: s.teamBillingType === 'direct' ? 'direct' : 'case_based',
    showOriginalTimesheetEntries: s.showOriginalTimesheetEntries === true,
    useOriginalTimesheetEntriesForPersonalHours:
      s.useOriginalTimesheetEntriesForPersonalHours === true
  };
}

export const saveGeneralSettings = (input: Partial<GeneralSettings>) =>
  post('/api/general-settings', input);

export const fetchTimezone = () =>
  get<unknown>('/api/user/timezone').then((r) => str(rec(r).timezone, 'America/New_York'));

export const saveTimezone = (timezone: string) => put('/api/user/timezone', { timezone });

// --------------------------------------------------------------------- team

export async function fetchTeamMembers(teamId: string, signal?: AbortSignal): Promise<TeamMember[]> {
  const raw = await get<unknown>(`/api/teams/${teamId}/members`, undefined, signal);
  return unwrapList(raw, 'members').map((v) => {
    const m = rec(v);
    const u = rec(m.user);
    return {
      id: str(m.id),
      userId: str(m.userId ?? u.id),
      name: strOrNull(u.name ?? m.name),
      email: strOrNull(u.email ?? m.email),
      image: strOrNull(u.image),
      role: m.role === 'admin' ? 'admin' : 'member',
      hourlyRate: m.hourlyRate === null || m.hourlyRate === undefined ? null : num(m.hourlyRate),
      joinedAt: str(m.createdAt)
    };
  });
}

export async function fetchTeamInvites(teamId: string, signal?: AbortSignal): Promise<TeamInvite[]> {
  const raw = await get<unknown>(`/api/teams/${teamId}/invites`, undefined, signal);
  return unwrapList(raw, 'invites').map((v) => {
    const i = rec(v);
    return {
      id: str(i.id),
      email: str(i.email),
      role: i.role === 'admin' ? 'admin' : 'member',
      createdAt: str(i.createdAt),
      expiresAt: strOrNull(i.expiresAt)
    };
  });
}

export const inviteTeamMember = (teamId: string, input: { email: string; role: string }) =>
  post(`/api/teams/${teamId}/invites`, input);

export const revokeTeamInvite = (teamId: string, inviteId: string) =>
  del(`/api/teams/${teamId}/invites/${inviteId}`);

export const removeTeamMember = (teamId: string, memberId: string) =>
  del(`/api/teams/${teamId}/members/${memberId}`);

export const updateTeamMemberRole = (teamId: string, memberId: string, role: string) =>
  patch(`/api/teams/${teamId}/members/${memberId}/update-role`, { role });

export const renameTeam = (teamId: string, name: string) => patch(`/api/teams/${teamId}`, { name });

// ------------------------------------------------------------- subscription

const PLAN_TIERS: readonly PlanTier[] = ['trial', 'expired', 'solo', 'team', 'firm', 'pro', 'enterprise'];

export async function fetchSubscription(signal?: AbortSignal): Promise<Subscription> {
  const [tierRaw, usageRaw] = await Promise.all([
    get<unknown>('/api/user/payment-tier', undefined, signal).catch(() => ({})),
    get<unknown>('/api/subscription/usage', undefined, signal).catch(() => ({}))
  ]);
  const tier = rec(tierRaw);
  // The server nests the counters under `usage` (GET /api/subscription/usage
  // → { currentPlan, usage: { invoices, seats, … }, limits }). Reading them off
  // the top level found nothing, so the page always showed 1 seat and
  // unlimited invoices. The mock serves them flat; accept both.
  const usageBody = rec(usageRaw);
  const usage = usageBody.usage ? rec(usageBody.usage) : usageBody;
  const invoices = rec(usage.invoices);
  const seats = rec(usage.seats);
  const status = str(tier.status, 'expired');
  const locked = tier.lockedPrice ? rec(tier.lockedPrice) : null;

  return {
    tier: PLAN_TIERS.includes(status as PlanTier) ? (status as PlanTier) : 'expired',
    status: status === 'trial' ? 'trialing' : status === 'expired' ? 'none' : 'active',
    seats: num(tier.numSeats, 1),
    trialEndsAt: strOrNull(tier.trialEndsAt),
    cardRequired: tier.cardRequired === true,
    lockedPrice: locked
      ? { cents: num(locked.cents), interval: str(locked.interval, 'year') }
      : null,
    currentPeriodEnd: strOrNull(tier.currentPeriodEnd),
    cancelAtPeriodEnd: tier.cancelAtPeriodEnd === true,
    usage: {
      invoicesThisMonth: num(invoices.used),
      invoiceLimit: num(invoices.limit, -1) === -1 ? null : num(invoices.limit),
      seatsUsed: num(seats.used, 1),
      seatsPending: num(seats.pending, 0)
    }
  };
}

export const startCheckout = (input: {
  plan: string;
  interval: 'monthly' | 'annual';
  success_url?: string;
  cancel_url?: string;
}) => post<{ url: string }>('/api/stripe/create-checkout-session', input);

/** Record the subscription a finished Checkout created, without waiting for the webhook. */
export const completeCheckout = (sessionId: string) =>
  post<{ ok: boolean; plan: string | null }>('/api/stripe/checkout-complete', { sessionId });

export const openCustomerPortal = (returnUrl: string) =>
  post<{ url: string }>('/api/stripe/customer-portal', { returnUrl });
